import { ReactiveElement, ReactiveState } from "@nrk/reactive-element";
import { throttle } from "throttle-debounce";
import { PlayerActions } from "../player/player-actions.js";
import { getActionElement, getElementOfType } from "../utils/dom.js";
import platform from "../utils/platform.js";
class ControlsElement extends ReactiveElement {
  static get reactiveAttributes() {
    return {
      ...super.reactiveAttributes,
      active: Boolean
    };
  }
  static get reactiveProperties() {
    return {
      ...super.reactiveProperties,
      fullscreenState: Object,
      isCurrentTimeLive: Boolean,
      isEnded: Boolean,
      isLive: Boolean,
      isPlaying: Boolean,
      airPlayState: Object,
      subtitleState: ReactiveState,
      interactionPointState: Object,
      pipState: Object,
      isStarted: Boolean,
      muted: Boolean
    };
  }
  /** Flag indicating whether the button bar is active (visible) */
  active = false;
  /** The current Fullscreen state reported by Player-Core (shadowed Player property) */
  fullscreenState;
  /** Flag indication we are watching the stream live (shadowed Player property) */
  isCurrentTimeLive = false;
  /** Flag indicating that media playback has completed (shadowed Player property) */
  isEnded = false;
  /** Flag indicating whether the media source is live or on-demand (shadowed Player property) */
  isLive = false;
  /** Flag indicating that media playback has started (shadowed Player property) */
  isPlaying = false;
  /** Flag indicating that media playback has started (shadowed Player property) */
  isStarted = false;
  /** shadowed interactionState from player */
  interactionPointState;
  /** airPlayState shadowed from player */
  airPlayState;
  /** All things subtitles (shadowed Player property) */
  subtitleState;
  /** Picture-in-picture state, shadowed from player */
  pipState;
  /** Flag indicating that audio is muted (shadowed Player property) */
  muted = false;
  /** Allow this element to be source of click events that trigger a "TogglePlay" action */
  action = PlayerActions.TogglePlay;
  /** Hide until playback started */
  hidden = true;
  activatedAt;
  /** Flag to determine if the interaction was triggered by an input device that supports hover */
  isHovering = true;
  /** Flag to determine if pointer is outside controls area */
  isPointerOutside = false;
  /** Flag to determine if user is currently pressing with input device */
  isPressing = false;
  /** The PlayerElement parent instance */
  player;
  /** Time in milliseconds until active controls will automatically deactivate */
  deactivateTimeout = 2e3;
  _deactivateTimeoutId;
  _throttledHandleEvent = throttle(60, this.handleEvent);
  _unshadowProperties;
  constructor() {
    super();
    this.deactivate = this.deactivate.bind(this);
  }
  readyCallback() {
    this.tabIndex = 0;
    this.player = getElementOfType(this);
    this.addEventListener("click", this);
    this.addEventListener("focus", this, true);
    this.addEventListener("blur", this, true);
    this.addEventListener(
      "touchstart",
      this.handleTouchStartHoverProxyEvent,
      platform.supportsPassiveEvents ? { passive: true } : void 0
    );
    this.addEventListener(platform.pointerEventTypes.enter, this);
    this.addEventListener(platform.pointerEventTypes.down, this);
    this.addEventListener(platform.pointerEventTypes.move, this._throttledHandleEvent);
    this._unshadowProperties = this.player.shadowProperties(
      [
        "fullscreenState",
        "pipState",
        "isCurrentTimeLive",
        "isEnded",
        "isLive",
        "isPlaying",
        "isStarted",
        "interactionPointState",
        "muted",
        "subtitleState"
      ],
      this
    );
  }
  disconnectedCallback() {
    this.removeEventListener("click", this);
    this.removeEventListener("focus", this, true);
    this.removeEventListener("blur", this, true);
    this.removeEventListener("touchstart", this.handleTouchStartHoverProxyEvent);
    this.removeEventListener(platform.pointerEventTypes.enter, this);
    this.removeEventListener(platform.pointerEventTypes.down, this);
    this.removeEventListener(platform.pointerEventTypes.move, this._throttledHandleEvent);
    this.removeEventListener(platform.pointerEventTypes.leave, this);
    document.removeEventListener(platform.pointerEventTypes.up, this);
    this._unshadowProperties?.();
    this.player = null;
    super.disconnectedCallback();
  }
  updating(propName, oldValue) {
    super.updating(propName, oldValue);
    if (propName === "isStarted" && this.isStarted) {
      this.hidden = false;
    }
  }
  handleEvent(event) {
    const { target, type } = event;
    if (type === "focus") {
      this.activate(true, type);
    } else if (type === "blur") {
      const relatedTarget = event.relatedTarget;
      if (this.isPlaying && (!relatedTarget || !this.contains(relatedTarget))) {
        this.activate(false, type);
      }
    } else if (type === "click") {
      if (getActionElement(target) !== this) {
        this.activate(true, "click nested actionElement");
      } else if (!this.isHovering) {
        const msSinceLastActivation = (/* @__PURE__ */ new Date()).getTime() - (this.activatedAt || 0);
        if (msSinceLastActivation > 100) {
          this.activate(!this.active, type);
        }
        event.preventDefault();
      }
      this.isHovering = true;
    } else {
      this.handlePointerEvent(event);
    }
  }
  handleTouchStartHoverProxyEvent() {
    this.isHovering = false;
  }
  handlePointerEvent(event) {
    const { target, type } = event;
    if (type === platform.pointerEventTypes.enter) {
      this.isPointerOutside = false;
      this.isPressing = false;
      this.addEventListener(platform.pointerEventTypes.move, this._throttledHandleEvent);
      this.addEventListener(platform.pointerEventTypes.leave, this);
    } else if (type === platform.pointerEventTypes.move) {
      if (!this.isPointerOutside) {
        this.activate(true, type);
      }
    } else if (type === platform.pointerEventTypes.down) {
      this.isPressing = true;
      this.removeEventListener(platform.pointerEventTypes.move, this._throttledHandleEvent);
      this.removeEventListener(platform.pointerEventTypes.leave, this);
      document.addEventListener(platform.pointerEventTypes.up, this);
    } else if (type === platform.pointerEventTypes.up) {
      this.isPressing = false;
      const isReleaseOutside = !this.contains(target);
      if (isReleaseOutside) {
        this.isHovering = true;
      } else if (this.isHovering) {
        this.addEventListener(platform.pointerEventTypes.move, this._throttledHandleEvent);
        this.addEventListener(platform.pointerEventTypes.leave, this);
      }
      if (this.isHovering || this.player.isSeeking) {
        this.activate(!isReleaseOutside, type);
      }
      document.removeEventListener(platform.pointerEventTypes.up, this);
    } else if (type === platform.pointerEventTypes.leave) {
      this.isPointerOutside = true;
      this.removeEventListener(platform.pointerEventTypes.move, this._throttledHandleEvent);
      this.removeEventListener(platform.pointerEventTypes.leave, this);
      const isCasting = Boolean(this.player?.activeSession?.getAdapterInfo().chromecast) || false;
      this.activate(isCasting || !this.isPlaying || this.player.focusVisible, type);
    }
  }
  /**
   * Set "active" state, triggering a delayed auto-deactivation in certain cases.
   * Note that this is handled outside of normal update lifecycle in order
   * to be able to delay auto-deactivation by repeatedly setting "active=true".
   */
  activate(value, _reason) {
    window.clearTimeout(this._deactivateTimeoutId);
    this.active = value;
    if (this.active) {
      this._deactivateTimeoutId = window.setTimeout(this.deactivate, this.deactivateTimeout);
      this.activatedAt = (/* @__PURE__ */ new Date()).getTime();
    }
  }
  deactivate() {
    const shouldDeactivate = this.isPlaying && !this.isPressing && !this.player.focusVisible;
    const isCasting = Boolean(this.player?.activeSession?.getAdapterInfo().chromecast) || false;
    if (shouldDeactivate && !isCasting) {
      this.active = false;
    }
  }
}
export {
  ControlsElement
};
